import { forwardRef, useCallback, useEffect, useRef, useImperativeHandle, useState } from 'react';
import { PlayerProps } from './types';
import {
  useShakaPlayer,
  useQualityControl,
  useSkipControls,
  useMuxAnalytics,
  useShakaUI,
  useEventHandlers,
  useLiveIndicator,
  useKeyboardControls,
  useAdEvents,
  usePlayerStoreSync,
  usePlayerInitialization,
  useDisableNativeControls,
  useScriptLoader,
  useSystem73,
  useAdsInitialization
} from './hooks';
import './styles.css';
import { hasPlaylistUrl } from './utils/playlist';
import { getPlayerStyles } from './utils/playerStyles';
import { Loading, ErrorScreen } from './components';
import { BigPlayIcon } from './icons';
import useMessages, { type Translations } from './messages/useMessages';

// Declare google namespace for IMA SDK
declare global {
  namespace google {
    namespace ima {
      class AdsRequest {
        adTagUrl: string;
      }
    }
  }
}

export const Player = forwardRef<HTMLVideoElement, PlayerProps>(
  ({ 
    src, 
    managedMode = false,
    autoPlay = false, 
    loop = false, 
    muted = false, 
    controls = true,
    poster,
    width,
    height,
    aspectRatio = 16/9,
    shakaConfig,
    drmConfig,
    muxConfig,
    system73Config,
    imaConfig,
    chromecastConfig,
    qualityConfig,
    seekbarConfig,
    iconSizes,
    events,
    locale = 'en',
    containerClassName,
    liveThresholdSeconds = 15,
    publicKey,
    auth,
    ...videoProps
  }, ref) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [hasStartedPlaying, setHasStartedPlaying] = useState(false);
    const [bfResetKey, setBfResetKey] = useState(0);
    // Translation key of a playback-blocking segment error (X-Error-Code) to
    // display over the player; null while playback is allowed.
    const [blockedMessageKey, setBlockedMessageKey] = useState<string | null>(null);
    // Playback died with no more specific screen to show (e.g. a failed
    // load); shows the default error screen instead of an endless spinner.
    const [hasFatalError, setHasFatalError] = useState(false);
    const { t } = useMessages(locale);

    const handlePlaybackBlocked = useCallback((messageKey: string) => {
      setBlockedMessageKey(messageKey);
      events?.onError?.(new Error(messageKey));
    }, [events]);

    const handleFatalError = useCallback(() => {
      setHasFatalError(true);
    }, []);

    // Load external scripts (IMA SDK, System73)
    const isScriptsLoaded = useScriptLoader({
      hasAds: !!imaConfig?.adTagUrl,
      hasSystem73: !!system73Config?.apiKey
    });

    // Managed mode optimization: prevents unnecessary reinitialization
    // When wrapper components (Video, Event, CreativeWork) refetch data, the src object
    // reference changes even if the playlist URL is the same. In managedMode, we only
    // reinitialize when hasPlaylist changes (boolean: playlist exists or doesn't exist),
    // rather than on every src object reference change.
    const hasPlaylist = hasPlaylistUrl(src);

    // Core Shaka Player hook - provides low-level player management
    // Returns methods for initialization, manifest loading, and cleanup
    // IMPORTANT: hasAds and hasSystem73 flags control automatic manifest loading
    // - When true: initializePlayer() skips auto-load, allowing System73/ads to initialize first
    // - When false: initializePlayer() loads manifest automatically
    // See ARCHITECTURE.md for detailed initialization sequence
    const { playerRef, initializePlayer, loadManifest, destroyPlayer, handleMuxError } = useShakaPlayer({
      src,
      shakaConfig,
      drmConfig,
      onError: events?.onError,
      onPlaybackBlocked: handlePlaybackBlocked,
      onFatalError: handleFatalError,
      onPlayerReady: events?.onPlayerReady,
      muxConfig,
      onMuxReady: events?.onMuxReady,
      onMuxDataUpdate: events?.onMuxDataUpdate,
      publicKey,
      mottoToken: auth?.mottoToken,
      hasAds: !!imaConfig?.adTagUrl,        // Skip auto-load if ads configured
      hasSystem73: !!system73Config?.apiKey, // Skip auto-load if System73 configured
      apiToken: auth?.apiToken
    });

    const {
      updateMuxData,
      destroyMux
    } = useMuxAnalytics(playerRef, events?.onMuxDataUpdate);

    // Forward reference pattern to break circular hook dependency:
    // - useQualityControl needs to call syncQualityChange in its callback
    // - usePlayerStoreSync (which provides syncQualityChange) needs setQuality from useQualityControl
    // Solution: Create ref now, populate it after usePlayerStoreSync, callback uses ref
    const syncQualityChangeRef = useRef<((quality: { height: number; bandwidth: number }) => void) | null>(null);

    const {
      getAvailableQualities,
      setQuality,
      setupQualityTracking,
      configureQuality
    } = useQualityControl(playerRef, qualityConfig, (quality) => {
      // Sync to store via ref (populated below after usePlayerStoreSync)
      syncQualityChangeRef.current?.(quality);
      // Call user's onQualityChange callback
      events?.onQualityChange?.(quality);
    });

    // Skip controls - core logic for jumping forward/backward in video
    // Separated from input binding to allow multiple input methods (keyboard, UI buttons, store, API)
    const { 
      skipBack, 
      skipForward, 
    } = useSkipControls(videoRef, events?.onSkipBack, events?.onSkipForward);

    // Keyboard input binding - maps arrow keys to skip functions
    useKeyboardControls(videoRef, {
      skipBack,
      skipForward,
      enabled: true
    });

    const { setupEventListeners, cleanupEventListeners } = useEventHandlers(videoRef, playerRef, events ?? {});

    const { uiRef, initializeUI, destroyUI } = useShakaUI(
      playerRef,
      containerRef,
      videoRef,
      controls,
      chromecastConfig,
      seekbarConfig,
      events?.onSkipBack,
      events?.onSkipForward,
      iconSizes,
      locale
    );

    const { getLiveStatus, seekToLiveEdge } = useLiveIndicator(containerRef, playerRef, {
      enabled: true,
      indicatorColor: '#ff0000',
      indicatorSize: 8,
      showPulseAnimation: true,
      liveThresholdSeconds,
      onLiveStatusChange: events?.onLiveStatusChange
    });

    const { setupAdEventListeners, cleanupAdEventListeners } = useAdEvents(playerRef, events ?? {});

    // Store synchronization (only active when wrapped in PlayerProvider)
    const { syncQualityChange, updateAvailableQualities } = usePlayerStoreSync({
      videoRef,
      playerRef,
      getLiveStatus,
      skipForward,
      skipBack,
      setQuality,
      getAvailableQualities,
      seekToLiveEdge,
    });

    // Populate the ref to complete the forward reference pattern (see syncQualityChangeRef above)
    syncQualityChangeRef.current = syncQualityChange;

    const { initializeSystem73 } = useSystem73(system73Config);

    const { initializeAds } = useAdsInitialization({
      imaConfig,
      autoPlay,
      playerRef,
      videoRef,
      uiRef,
      setupAdEventListeners,
      loadManifest
    });

    // Reinitialize on back/forward cache restore without double-initializing on first load
    useEffect(() => {
      const onPageShow = (e: any) => {
        if (e && e.persisted) {
          setBfResetKey((k) => k + 1);
        }
      };
      window.addEventListener('pageshow', onPageShow);
      return () => window.removeEventListener('pageshow', onPageShow);
    }, []);

    // Main initialization - orchestrates all player setup in the correct sequence
    // See usePlayerInitialization.ts for detailed documentation on initialization order
    usePlayerInitialization({
      videoRef,
      playerRef,
      isScriptsLoaded,
      setIsInitialLoading,
      system73Config,
      shakaConfig,
      imaConfig,
      initializeSystem73,
      initializePlayer,
      loadManifest,
      setupEventListeners,
      setupQualityTracking,
      configureQuality,
      updateAvailableQualities,
      initializeUI,
      initializeAds,
      handleMuxError,
      cleanupEventListeners,
      cleanupAdEventListeners,
      destroyUI,
      destroyMux,
      destroyPlayer,
      src,
      hasPlaylist,
      managedMode,
      bfResetKey,
    });

    // Update video properties when they change
    useEffect(() => {
      const video = videoRef.current;
      if (!video) return;

      video.autoplay = autoPlay;
      video.loop = loop;
      if (poster) video.poster = poster;
    }, [autoPlay, loop, muted, poster, imaConfig?.adTagUrl]);

    // Track first play so poster overlay hides when playback starts
    useEffect(() => {
      const video = videoRef.current;
      if (!video || !poster) return;
      const onPlay = () => setHasStartedPlaying(true);
      video.addEventListener('play', onPlay);
      return () => video.removeEventListener('play', onPlay);
    }, [poster]);

    // Reset poster overlay and any blocking error when src changes
    useEffect(() => {
      setHasStartedPlaying(false);
      setBlockedMessageKey(null);
      setHasFatalError(false);
    }, [src]);

    // Prevent native controls from conflicting with Shaka UI
    useDisableNativeControls(videoRef);

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      ...videoRef.current!,
      // Custom methods for quality control
      getAvailableQualities,
      setQuality,
      // Skip methods
      skipBack,
      skipForward,
      // Mux methods
      updateMuxData,
      // Live status methods
      getLiveStatus,
      seekToLiveEdge,
      // Access to underlying instances
      getPlayer: () => playerRef.current,
      getMuxMonitor: () => null
    }), [getAvailableQualities, setQuality, skipBack, skipForward, updateMuxData, getLiveStatus, seekToLiveEdge]);

    const { isResponsive, containerClasses, containerStyle, videoClasses, videoStyle } = getPlayerStyles({
      width,
      height,
      aspectRatio,
      containerClassName
    });

    // Filter out controls prop to prevent conflicts with native HTML5 controls
    const filteredVideoProps = { ...videoProps };
    delete (filteredVideoProps as any).controls;

    return (
      <div 
        ref={containerRef}
        className={containerClasses}
        style={containerStyle}
      >
        <video
          ref={videoRef}
          className={videoClasses}
          width={isResponsive ? undefined : width}
          height={isResponsive ? undefined : height}
          style={videoStyle}
          controls={false}
          playsInline={true}
          {...filteredVideoProps}
        />

        {poster && !hasStartedPlaying && (
          <div className="absolute inset-0" style={{ zIndex: 10 }}>
            <img src={poster} alt="" className="w-full h-full object-cover" />
            <button
              className="absolute inset-0 flex items-center justify-center w-full h-full bg-transparent border-0 cursor-pointer"
              onClick={() => videoRef.current?.play()}
              aria-label="Play"
            >
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors">
                <BigPlayIcon size={32} />
              </div>
            </button>
          </div>
        )}

        {isInitialLoading && !blockedMessageKey && !hasFatalError && (
          <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
            <Loading className="bg-transparent" />
          </div>
        )}

        {blockedMessageKey && (
          <div className="absolute inset-0 z-30">
            <ErrorScreen
              title={t(blockedMessageKey as keyof Translations)}
              description={t(`${blockedMessageKey}_DESCRIPTION` as keyof Translations)}
            />
          </div>
        )}

        {hasFatalError && !blockedMessageKey && (
          <div className="absolute inset-0 z-30">
            <ErrorScreen
              title={t('DEFAULT_ERROR')}
              description={t('DEFAULT_ERROR_DESCRIPTION')}
            />
          </div>
        )}
      </div>
    );
  }
);

Player.displayName = 'Player'; 